"use client"

import { useEffect } from "react"
import { useRibbonActions } from "@/components/editor/ribbon/use-ribbon-actions"
import { useEditorStore } from "@/lib/editor-store"

function isMod(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey
}

/**
 * Registers document-level shortcuts when Monaco is focused / available.
 * Avoids conflicting with inputs outside the editor.
 */
export function useRibbonShortcuts() {
  const actions = useRibbonActions()
  const { getActiveMonacoEditor } = useEditorStore()

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const ed = getActiveMonacoEditor()
      if (!ed) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      // Allow when focus is in Monaco (textarea) or body; skip other form fields
      const inMonaco =
        target?.closest?.(".monaco-editor") != null ||
        target?.classList?.contains("inputarea")
      if (!inMonaco && (tag === "input" || tag === "textarea" || target?.isContentEditable)) {
        return
      }
      if (!inMonaco && tag !== "body" && target !== document.body) {
        // Still allow when editor has text focus via Monaco's hidden textarea
        if (!ed.hasTextFocus()) return
      }

      if (isMod(e) && !e.shiftKey && !e.altKey) {
        const k = e.key.toLowerCase()
        if (k === "b") {
          e.preventDefault()
          actions.bold()
          return
        }
        if (k === "i") {
          e.preventDefault()
          actions.italic()
          return
        }
        if (k === "u") {
          e.preventDefault()
          actions.underline()
          return
        }
      }

      if (isMod(e) && e.shiftKey && !e.altKey) {
        const k = e.key.toLowerCase()
        if (k === "l") {
          e.preventDefault()
          actions.alignLeft()
          return
        }
        if (k === "c") {
          e.preventDefault()
          actions.alignCenter()
          return
        }
        if (k === "r") {
          e.preventDefault()
          actions.alignRight()
          return
        }
        if (k === "j") {
          e.preventDefault()
          actions.justify()
          return
        }
        if (e.key === "8" || e.code === "Digit8") {
          e.preventDefault()
          actions.bulletList()
          return
        }
        if (e.key === "7" || e.code === "Digit7") {
          e.preventDefault()
          actions.numberList()
          return
        }
        if (k === "enter") {
          // Ctrl+Shift+Enter → page break (common Word-like)
          e.preventDefault()
          actions.breaks()
          return
        }
      }

      // Ctrl+Enter → page break
      if (isMod(e) && !e.shiftKey && !e.altKey && e.key === "Enter") {
        e.preventDefault()
        actions.pageBreak()
        return
      }
    }

    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [actions, getActiveMonacoEditor])
}
